import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';
import { queryKeys } from '@/lib/queryKeys';
import { relativeDay } from '@/pages/home/widgets/format';
import { WidgetFrame, WidgetRow } from '@/pages/home/widgets/WidgetFrame';

/**
 * 보미소식 — <b>최근 일주일의 생일자</b>와 <b>경조사</b> (사용자 지시 2026-08-28).
 *
 * 생일은 지난 일주일치가 함께 선다(금요일이면 이번 주말까지). 그날 하루로 접으면 휴가로 하루
 * 비운 사람은 영영 못 보고, 무엇보다 위젯이 빈 날이 잦아진다. 경조사가 얼마나 서 있을지는
 * <b>글쓴이가 고른다</b> — 일주일·2주일·한 달(`NoticePost.widgetDays`).
 *
 * <h2>공지사항 위젯과 나눈 이유</h2>
 * 저쪽은 최근 5건을 제목만 흘려보내는 자리라, 생일·경조사가 섞이면 계약·착공 공지에 밀려
 * 그날 안 보인다. 축하는 그날 못 보면 없던 일이 된다 — 그래서 자기 칸을 준다.
 *
 * <h2>제목이 아니라 이름을 보여 준다</h2>
 * 생일자의 이름과 소속은 서버가 인력 표에서 직접 세어 준다(`GET /v1/notices/bomi-news`). 안내 글의
 * 본문을 여기서 파싱하지 않는 이유는 문장을 다듬을 때마다 위젯이 깨지기 때문이다.
 *
 * <h2>안내 글이 없는 날은 서지 않는다</h2>
 * 이름은 인력 표에서 오지만 <b>설 자격은 글에서</b> 온다(사용자 지시 2026-09-08). 예전에는 글을 지워도
 * 위젯에 줄이 남아, 지운 사람 눈에는 지워지지 않은 것처럼 보였다. 이 갈래는 서버가 판단하므로
 * (`BomiNewsService`) 여기서는 받은 것을 그대로 그리면 된다.
 */

type BomiNews = {
  birthdays: {
    name: string;
    deptSite: string | null;
    date: string;
    today: boolean;
    /**
     * 그 날짜를 안고 있는 안내 글. <b>항상 있다</b> — 글이 없는 날짜는 서버가 목록에서 빼고 보낸다
     * (사용자 지시 2026-09-08).
     */
    noticeId: string;
  }[];
  condolences: {
    id: string;
    title: string;
    /** 누구의 소식인가. 글쓴이가 대상자를 고르지 않았으면 없다. */
    subjectName: string | null;
    subjectDeptSite: string | null;
    commentCount: number;
    createdAt: string;
    /** 글이 올라온 날(서버 시계로 자른 값). 생일 줄과 섞어 세우는 축이다. */
    date: string;
    today: boolean;
  }[];
};

type Birthday = BomiNews['birthdays'][number];

type Condolence = BomiNews['condolences'][number];

/** 생일이든 경조사든, 줄 순서를 정할 때 보는 것은 이 둘뿐이다. */
type Row = ({ kind: 'birthday'; person: Birthday } | { kind: 'condolence'; post: Condolence }) & {
  date: string;
  today: boolean;
};

export function BomiNewsWidget() {
  const news = useQuery({
    queryKey: queryKeys.bomiNews,
    queryFn: () => apiFetch<BomiNews>('/v1/notices/bomi-news'),
  });

  const birthdays = news.data?.birthdays ?? [];
  const condolences = news.data?.condolences ?? [];

  const rows: Row[] = [
    ...birthdays.map((person) => ({ kind: 'birthday' as const, person, date: person.date, today: person.today })),
    ...condolences.map((post) => ({ kind: 'condolence' as const, post, date: post.date, today: post.today })),
  ].sort(byReadingOrder);

  return (
    <WidgetFrame
      title="보미소식"
      icon="users"
      to="/news/notices"
      pending={news.isPending}
      error={news.error}
      empty={rows.length === 0}
      emptyText="최근 일주일 사이 생일자와 경조사가 없습니다."
    >
      {/*
        머리글도 구분선도 없이 <b>한 목록</b>이다(사용자 지시 2026-08-28).

        「생일」·「경조사」 라고 적을 이유가 없다 — 줄 자체가 이미 「… 님의 생일을 축하합니다」·
        「[결혼] …」 라고 말한다. 선을 그을 이유도 없다: 둘 다 사람 소식이고, 가르는 순간 위젯이
        두 칸처럼 읽힌다.

        그래서 순서도 <b>섞는다</b>(사용자 지적 2026-09-08). 생일 배열을 다 그린 뒤 경조사 배열을
        그리면, 「한 목록」 이라고 해 놓고 일주일 전 생일이 오늘 올라온 부고를 늘 밀어낸다.
      */}
      <div className="space-y-0.5">
        {rows.map((row) =>
          row.kind === 'birthday' ? (
            <WidgetRow
              key={`b:${row.person.name}-${row.person.date}`}
              // 그 사람이 실린 글을 <b>펼친 채로</b> 연다(사용자 지적 2026-09-04). 목록으로만 보내면
              // 방금 위젯에서 읽은 이름을 목록에서 다시 찾아야 한다 — 위젯이 이름까지 말해 놓고
              // 마지막 한 걸음을 떠넘기는 셈이다.
              to={`/news/notices?post=${row.person.noticeId}`}
              // 소속을 모르면 배지 자체를 빼고 이름만 세운다 — 「소속 미정」 이라고 적는 것은
              // 축하 자리에 우리 자료의 빈칸을 밀어 넣는 일이다(사용자 지적 2026-08-28).
              leading={
                row.person.deptSite ? (
                  <span className="badge badge-gray flex-shrink-0 badge-sm">
                    {row.person.deptSite}
                  </span>
                ) : undefined
              }
              title={`${row.person.name} 님의 생일을 축하합니다`}
              // 목록에는 지난 일주일과 (금요일이면) 이번 주말이 함께 선다. 오늘이 아닌 줄은
              // 언제인지 짚어 준다 — 짚지 않으면 전부 「오늘 생일」 로 읽힌다.
              meta={row.today ? '오늘' : dateLabel(row.date)}
            />
          ) : (
            <WidgetRow
              key={`c:${row.post.id}`}
              to={`/news/notices?post=${row.post.id}`}
              // 경조사도 누군가의 일이다(사용자 지시 2026-08-28). 글쓴이가 대상자를 골랐으면
              // 생일 줄과 같은 자리에 같은 배지가 선다. 안 골랐으면 제목만 — 이름을 제목에서
              // 파내려 들지 않는다(글쓴이가 어떻게 적었느냐에 달린 문자열이다).
              leading={
                row.post.subjectDeptSite ? (
                  <span className="badge badge-gray flex-shrink-0 badge-sm">
                    {row.post.subjectDeptSite}
                  </span>
                ) : undefined
              }
              title={row.post.subjectName ? `${row.post.subjectName} 님 · ${row.post.title}` : row.post.title}
              // 섞인 목록에서는 <b>날짜가 정렬의 근거</b>다. 댓글 수를 이 자리에 넣던 때는 경조사가
              // 따로 서 있어 상관없었지만, 지금은 날짜 없는 줄 하나가 순서를 어겨 보이게 만든다.
              meta={relativeDay(row.post.createdAt)}
            />
          ),
        )}
      </div>
    </WidgetFrame>
  );
}

/**
 * 오늘이 맨 위, 그다음이 날짜 최신순, 같은 날이면 생일이 먼저.
 *
 * <p>서버가 이미 각 목록 안을 정렬해 주지만({@code BomiNewsService#byReadingOrder}) 두 목록을
 * 합치는 것은 화면의 몫이라 여기서 한 번 더 세운다. 「오늘」 을 앞으로 뽑는 이유는 서버 쪽과
 * 같다 — 금요일에 미리 세운 주말 생일이 오늘 소식을 밀어내면 안 된다.
 */
function byReadingOrder(left: Row, right: Row): number {
  if (left.today !== right.today) return left.today ? -1 : 1;
  if (left.date !== right.date) return left.date > right.date ? -1 : 1;
  if (left.kind === right.kind) return 0;
  return left.kind === 'birthday' ? -1 : 1;
}

/** 「8/29(토)」. 오늘이 아닌 생일은 날짜와 요일을 함께 적어야 지난 것인지 앞으로인지가 선다. */
function dateLabel(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleDateString('ko-KR', { month: 'numeric', day: 'numeric', weekday: 'short' });
}
